import { useCallback, useEffect, useState } from 'react';
import { Form as FormikForm, Formik } from 'formik';
import { Button, Card, ListGroup } from 'react-bootstrap';
import API from '../../Helpers/API';
import SellerService from '../../Helpers/SellerService';
import { sanitizePhone, plaintextPhone } from '../../Helpers/phoneHelpers';
import { sellerValidation } from '../../Helpers/ValidationSchemas/validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import ProfessionsService from '../../Helpers/ProfessionsService';
import ProfessionsDropdown from '../ProfessionsDropdown';
import AlertErrors from '../AlertErrors';
import SellerFormChangeObserver from './SellerFormChangeObserver';
import TextInput from '../TextInput';
import PhoneInput from '../PhoneInput';
import ModalConfirm from '../ModalConfirm';
import { catchApiResponseError } from '../../Helpers/catchApiResponseError';

const EditSellerProfile = ({
  seller,
  setEdit,
  setShowSuccess,
  setSuccessMessage,
  getSeller,
  setShowSpinner,
}) => {
  const [formHasChanged, setFormHasChanged] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [professions, setProfessions] = useState([]);

  const getProfessions = useCallback(async () => {
    try {
      const response = await new ProfessionsService(new API()).getProfessions();
      setProfessions(response.data);
    } catch (err) {
      catchApiResponseError(err, setErrorMessages, setShowErrors);
    }
  }, []);

  const handleSubmit = async (input) => {
    try {
      setShowSpinner(true);
      setShowErrors(false);
      setShowSuccess(false);
      const response = await new SellerService(new API()).updateSeller({
        id: seller.id,
        name: input.name,
        email: input.email,
        phoneNumber: sanitizePhone(input.phone),
        professionId: input.profession,
      });
      if (response.status > 199 && response.status < 300) {
        setSuccessMessage('Seller updated');
        setShowSuccess(true);
        getSeller();
        setEdit(false);
      }
      response && setShowSpinner(false);
    } catch (err) {
      catchApiResponseError(err, setErrorMessages, setShowErrors);
      setShowSpinner(false);
    }
  };

  useEffect(() => {
    getProfessions();
  }, [getProfessions]);

  return (
    <>
      <AlertErrors show={showErrors} message={errorMessages} />
      <Formik
        enableReinitialize
        onSubmit={handleSubmit}
        initialValues={{
          name: seller.name ?? '',
          email: seller.email ?? '',
          phone: seller.phoneNumber ? plaintextPhone(seller.phoneNumber) : '',
          profession: seller.professionId ?? '',
        }}
        validationSchema={sellerValidation}
        validateOnMount
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          resetForm,
          touched,
          isValid,
          values,
          errors,
        }) => (
          <FormikForm onSubmit={handleSubmit}>
            <SellerFormChangeObserver
              seller={seller}
              setFormHasChanged={setFormHasChanged}
            />
            <Card className="profilecard">
              <Card.Header className="profilecard__header">
                <ListGroup variant="flush" className="profilecard__listgroup">
                  <ListGroup.Item className="profilecard__listgroup-item position-relative justify-content-between align-items-center">
                    <div className="profilecard__header-wrapper">
                      <FontAwesomeIcon
                        icon={faUser}
                        title="user"
                        className="profilecard__header-icon"
                      />

                      <TextInput
                        fieldName="name"
                        label="Name"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        values={values}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                    <Button
                      className="profilecard__btn profilecard__btn--edit"
                      onClick={() => {
                        resetForm();
                        setFormHasChanged(false);
                      }}
                      title="Reset Form"
                      disabled={!formHasChanged}
                    >
                      <FontAwesomeIcon
                        icon={solid('rotate-left')}
                        title="Reset Form"
                        className="profilecard__btn-icon m-0"
                      />
                    </Button>
                  </ListGroup.Item>
                </ListGroup>
              </Card.Header>

              <Card.Body className="profilecard__body">
                <ListGroup variant="flush" className="profilecard__listgroup">
                  <ListGroup.Item className="profilecard__listgroup-item">
                    <FontAwesomeIcon
                      icon={solid('file')}
                      title="file"
                      className="profilecard__listgroup-icon"
                    />
                    <p className="mb-0">
                      <strong>Account:&emsp;</strong>
                      <br className="d-md-none" />
                      OLT-{String(seller.id).padStart(4, 0)}
                    </p>
                  </ListGroup.Item>
                  <ListGroup.Item className="profilecard__listgroup-item">
                    <FontAwesomeIcon
                      icon={solid('phone')}
                      title="phone"
                      className="profilecard__listgroup-icon"
                    />

                    <PhoneInput
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      values={values}
                      touched={touched}
                      errors={errors}
                    />
                  </ListGroup.Item>
                  <ListGroup.Item className="profilecard__listgroup-item">
                    <FontAwesomeIcon
                      icon={solid('envelope')}
                      title="envelope"
                      className="profilecard__listgroup-icon"
                    />

                    <TextInput
                      fieldName="email"
                      label="Email"
                      type="email"
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      values={values}
                      touched={touched}
                      errors={errors}
                    />
                  </ListGroup.Item>
                  <ListGroup.Item className="profilecard__listgroup-item">
                    <FontAwesomeIcon
                      icon={solid('toolbox')}
                      title="toolbox"
                      className="profilecard__listgroup-icon"
                    />

                    <ProfessionsDropdown
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      professions={professions}
                      values={values}
                      touched={touched}
                      errors={errors}
                    />
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>

              <Card.Footer className="p-0">
                {isValid}
                <Button
                  className="profilecard__btn"
                  onClick={() => setShowConfirm(true)}
                  disabled={!isValid || !formHasChanged}
                >
                  <FontAwesomeIcon
                    icon={solid('check')}
                    className="profilecard__btn-icon"
                  />
                  OK
                </Button>
                <Button
                  className="profilecard__btn"
                  variant="outline-primary"
                  onClick={() => setEdit(false)}
                >
                  <FontAwesomeIcon
                    icon={solid('ban')}
                    className="profilecard__btn-icon"
                  />
                  Cancel
                </Button>
              </Card.Footer>
            </Card>
            <ModalConfirm
              show={showConfirm}
              title="Confirm Update"
              text="Are you sure you want to update this seller?"
              handleClose={() => setShowConfirm(false)}
              confirmButtonText="Confirm"
              confirmClick={handleSubmit}
            />
          </FormikForm>
        )}
      </Formik>
    </>
  );
};

export default EditSellerProfile;
